import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/supabase';
import { readCache, writeCache } from '../utils/cacheManager';

type TableName = keyof Database['public']['Tables'];
type TableRow<T extends TableName> = Database['public']['Tables'][T]['Row'];

interface UseSupabaseTableResult<T extends TableName> {
  data: TableRow<T>[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateRecord: (id: string, updates: Partial<TableRow<T>>) => Promise<void>;
  insertRecord: (record: Partial<TableRow<T>>) => Promise<void>;
  deleteRecord: (id: string) => Promise<void>;
}

function getCacheKey(tableName: string, options?: any): string {
  return `supabase_table_${tableName}_${JSON.stringify(options || {})}`;
}

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

function isRetryableError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : (err as any)?.message ?? '').toLowerCase();
  const code = (err as any)?.code ?? '';
  if (!code && (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed') ||
    msg.includes('err_connection') ||
    msg.includes('network request failed') ||
    msg.includes('aborted')
  )) {
    return true;
  }
  return false;
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null && 'message' in err && typeof (err as any).message === 'string') {
    return (err as any).message;
  }
  return 'Unknown error';
}

export function useSupabaseTable<T extends TableName>(
  tableName: T,
  options?: {
    orderBy?: string;
    ascending?: boolean;
    secondaryOrderBy?: string;
    secondaryAscending?: boolean;
    limit?: number;
    filters?: Array<{ column: string; operator: string; value: any }>;
    primaryKey?: string;
    debounceMs?: number;
    columns?: string;
  }
): UseSupabaseTableResult<T> {
  const cacheKey = getCacheKey(tableName, options);

  const [data, setData] = useState<TableRow<T>[]>(() => {
    const cached = readCache(cacheKey);
    if (cached) return cached as TableRow<T>[];
    return [];
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const initialFetchDoneRef = useRef(false);

  const fetchData = useCallback(async (isBackgroundUpdate = false) => {
    if (!isBackgroundUpdate) {
      setIsLoading(true);
    }
    setError(null);

    let lastError: unknown = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (!isMountedRef.current) return;

      if (attempt > 0) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
        console.warn(`[useSupabaseTable] Retrying ${tableName} (attempt ${attempt}/${MAX_RETRIES}) in ${delay}ms`);
        await new Promise(r => setTimeout(r, delay));
        if (!isMountedRef.current) return;
      }

      try {
        const PAGE_SIZE = 1000;
        const desiredLimit = options?.limit || PAGE_SIZE;

        const buildBaseQuery = () => {
          let q = supabase.from(tableName).select(options?.columns || '*');

          if (options?.filters) {
            options.filters.forEach(filter => {
              if (filter.operator === 'eq') {
                q = q.eq(filter.column, filter.value);
              } else {
                q = q.filter(filter.column, filter.operator, filter.value);
              }
            });
          }

          if (options?.orderBy) {
            q = q.order(options.orderBy, { ascending: options.ascending ?? true });
          }

          const tiebreaker = options?.secondaryOrderBy || options?.primaryKey;
          if (tiebreaker && tiebreaker !== options?.orderBy) {
            q = q.order(tiebreaker, { ascending: options?.secondaryAscending ?? true });
          }

          return q;
        };

        let allRows: any[] = [];

        if (desiredLimit <= PAGE_SIZE) {
          const query = buildBaseQuery().limit(desiredLimit);
          const { data: fetchedData, error: fetchError } = await query;
          if (fetchError) throw fetchError;
          allRows = fetchedData || [];
        } else {
          let offset = 0;
          while (offset < desiredLimit) {
            if (!isMountedRef.current) return;
            const end = Math.min(offset + PAGE_SIZE - 1, desiredLimit - 1);
            const query = buildBaseQuery().range(offset, end);
            const { data: chunk, error: fetchError } = await query;
            if (fetchError) throw fetchError;
            const rows = chunk || [];
            allRows = allRows.concat(rows);
            if (rows.length < PAGE_SIZE) break;
            offset += PAGE_SIZE;
          }
        }

        const typedData = allRows as TableRow<T>[];

        if (isMountedRef.current) {
          setData(typedData);
          setIsLoading(false);
          setError(null);
          initialFetchDoneRef.current = true;
          writeCache(cacheKey, typedData as any[]);
        }
        return;
      } catch (err) {
        lastError = err;
        if (!isRetryableError(err) || attempt === MAX_RETRIES) {
          break;
        }
      }
    }

    console.error(`[useSupabaseTable] Error fetching ${tableName} after ${MAX_RETRIES} retries:`, lastError);
    if (isMountedRef.current) {
      const hasCachedData = initialFetchDoneRef.current || data.length > 0;
      if (!hasCachedData) {
        setError(extractErrorMessage(lastError));
      }
      if (!initialFetchDoneRef.current) {
        setIsLoading(false);
      }
    }
  }, [tableName, cacheKey, options?.orderBy, options?.ascending, options?.secondaryOrderBy, options?.secondaryAscending, options?.limit, options?.columns, JSON.stringify(options?.filters)]);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchData(true);
    }, options?.debounceMs ?? 500);
  }, [fetchData]);

  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;
  const debouncedFetchRef = useRef(debouncedFetch);
  debouncedFetchRef.current = debouncedFetch;

  useEffect(() => {
    isMountedRef.current = true;
    initialFetchDoneRef.current = false;

    const hasCache = data.length > 0;
    if (hasCache) {
      setIsLoading(false);
      fetchDataRef.current(true);
    } else {
      fetchDataRef.current(false);
    }

    const channel = supabase
      .channel(`${tableName}_changes_${Math.random()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: tableName,
        },
        () => {
          debouncedFetchRef.current();
        }
      )
      .subscribe();

    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [tableName, cacheKey]);

  const updateRecord = useCallback(
    async (id: string, updates: Partial<TableRow<T>>) => {
      try {
        const pkField = options?.primaryKey || 'id';
        const { error: updateError } = await supabase
          .from(tableName)
          .update(updates as any)
          .eq(pkField, id);

        if (updateError) {
          throw updateError;
        }

        await fetchData();
      } catch (err) {
        console.error(`[useSupabaseTable] Error updating ${tableName}:`, err);
        throw err;
      }
    },
    [tableName, fetchData, options?.primaryKey]
  );

  const insertRecord = useCallback(
    async (record: Partial<TableRow<T>>) => {
      try {
        const { error: insertError } = await supabase
          .from(tableName)
          .insert(record as any);

        if (insertError) {
          throw insertError;
        }

        await fetchData();
      } catch (err) {
        console.error(`[useSupabaseTable] Error inserting into ${tableName}:`, err);
        throw err;
      }
    },
    [tableName, fetchData]
  );

  const deleteRecord = useCallback(
    async (id: string) => {
      try {
        const pkField = options?.primaryKey || 'id';
        const { error: deleteError } = await supabase
          .from(tableName)
          .delete()
          .eq(pkField, id);

        if (deleteError) {
          throw deleteError;
        }

        await fetchData();
      } catch (err) {
        console.error(`[useSupabaseTable] Error deleting from ${tableName}:`, err);
        throw err;
      }
    },
    [tableName, fetchData, options?.primaryKey]
  );

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    updateRecord,
    insertRecord,
    deleteRecord,
  };
}
